import { darkTheme } from '../../../lib/tokens';
import type { UserLearningData } from './api';
import { formatHmm, formatRelative } from './format';

interface Props {
  data: UserLearningData;
}

// Verdichteter Hero-Strip: Persona-Chip, Streak, Lessons-Fortschritt, Quiz-Quote,
// Lernzeit, letzte Aktivitaet. Eine Zeile ueber alle KPIs — keine Charts hier.

export function LearningHero({ data }: Props): JSX.Element {
  const lessonsPct =
    data.lessons.total > 0 ? Math.round((data.lessons.completed / data.lessons.total) * 100) : 0;

  return (
    <div
      className="grid grid-cols-2 gap-3 rounded-md border p-4 md:grid-cols-3 lg:grid-cols-6"
      style={{
        background: darkTheme.colors.bgSurface,
        borderColor: darkTheme.colors.borderSubtle,
      }}
    >
      <PersonaCell persona={data.hero.persona} />
      <Cell
        label="Streak"
        primary={`${data.hero.streak.currentStreak}`}
        secondary={`längste ${data.hero.streak.longestStreak} Tage`}
      />
      <Cell
        label="Letzte Aktivität"
        primary={data.hero.lastActiveAt ? formatRelative(data.hero.lastActiveAt) : '—'}
        secondary={data.hero.lastActiveAt ? new Date(data.hero.lastActiveAt).toLocaleDateString('de-DE') : 'noch nie'}
      />
      <Cell
        label="Lektionen"
        primary={`${data.lessons.completed} / ${data.lessons.total}`}
        secondary={`${lessonsPct}% · in Arbeit ${data.lessons.inProgress}`}
      />
      <Cell
        label="Lernzeit gesamt"
        primary={formatHmm(data.sessions.totalElapsedSeconds + data.lessons.totalTimeSpentSeconds)}
        secondary={`${data.sessions.total} Sessions`}
      />
      <Cell
        label="Quiz-Quote"
        primary={data.quiz.total > 0 ? `${Math.round(data.quiz.correctRate * 100)}%` : '—'}
        secondary={data.quiz.total > 0 ? `${data.quiz.correct} / ${data.quiz.total} richtig` : 'kein Quiz'}
      />
    </div>
  );
}

function PersonaCell({ persona }: { persona: UserLearningData['hero']['persona'] }): JSX.Element {
  const name = persona.name ?? 'Keine Persona';
  const initial = name.charAt(0).toUpperCase();
  const avatarColor =
    persona.key === 'anna'
      ? '#F472B6'
      : persona.key === 'jan'
      ? '#60A5FA'
      : darkTheme.colors.textTertiary;

  return (
    <div className="flex items-center gap-3">
      <div
        className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-base font-bold text-white"
        style={{ background: avatarColor }}
        aria-label={`Persona ${name}`}
      >
        {initial}
      </div>
      <div className="min-w-0">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-text-tertiary">
          Persona
        </div>
        <div className="truncate text-base font-semibold text-text-primary">{name}</div>
      </div>
    </div>
  );
}

function Cell({
  label,
  primary,
  secondary,
}: {
  label: string;
  primary: string;
  secondary?: string;
}): JSX.Element {
  return (
    <div className="min-w-0">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-text-tertiary">
        {label}
      </div>
      <div className="truncate text-lg font-bold tabular text-text-primary">{primary}</div>
      {secondary && (
        <div className="truncate text-[11px] text-text-tertiary">{secondary}</div>
      )}
    </div>
  );
}
